/**
 * User RAG Page - Personal Document Upload & Search (for Guest users)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Description as FileIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';
import config from '../config';
import { getRagErrorMessage } from '../utils/errorHandler';

const API_URL = config.apiUrl;

export default function UserRAGPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useTranslation(['common', 'rag']);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState('general');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [stats, setStats] = useState(null);

  // Get auth header helper
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Load user documents
  const loadDocuments = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/user-documents/`, {
        headers: getAuthHeader(),
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  }, [getAuthHeader]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/user-documents/stats/summary`, {
        headers: getAuthHeader(),
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadDocuments();
    loadStats();
  }, [loadDocuments, loadStats]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = [
      'application/pdf',
      'text/markdown',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (allowedTypes.includes(file.type) || file.name.endsWith('.md') || file.name.endsWith('.txt')) {
      setSelectedFile(file);
      setUploadStatus(null);
    } else {
      setUploadStatus({
        type: 'error',
        message: t('notifications.invalidFile', { ns: 'rag' }),
      });
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus({ type: 'error', message: t('notifications.noFile', { ns: 'rag' }) });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category', category);

    try {
      const response = await axios.post(`${API_URL}/api/v1/user-documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        type: 'success',
        message: t('notifications.uploadSuccess', { ns: 'rag', id: response.data.document?.id }),
        data: response.data,
      });

      setSelectedFile(null);
      loadDocuments();
      loadStats();
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: getRagErrorMessage(error, t),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    setSearching(true);
    setSearchResults(null);

    const formData = new FormData();
    formData.append('query', searchQuery);
    formData.append('top_k', '5');

    try {
      const response = await axios.post(`${API_URL}/api/v1/user-documents/search`, formData, {
        headers: getAuthHeader(),
      });

      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({
        error: t('errors.rag.searchFailed'),
      });
    } finally {
      setSearching(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return t('common.na');
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/user-documents/${documentToDelete.id}`, {
        headers: getAuthHeader(),
      });
      
      setUploadStatus({
        type: 'success',
        message: t('notifications.deleteSuccess', { ns: 'rag', filename: documentToDelete.filename }),
      });
      
      loadDocuments();
      loadStats();
    } catch (error) {
      console.error('Delete error:', error);
      setUploadStatus({
        type: 'error',
        message: getRagErrorMessage(error, t),
      });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <IconButton
            onClick={() => navigate('/chat')}
            sx={{ color: 'white' }}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            📚 {t('userRag.title', { ns: 'rag', defaultValue: 'My Documents' })}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.9, ml: 6 }}>
          {t('userRag.subtitle', { ns: 'rag', defaultValue: 'Upload and search your personal documents' })}
        </Typography>
        {stats && (
          <Box sx={{ mt: 2, ml: 6, display: 'flex', gap: 2 }}>
            <Chip
              label={`${stats.total_documents || 0} documents`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label={`${stats.total_chunks || 0} chunks`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        )}
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Upload Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f57c00' }}>
                  📤 {t('upload.title', { ns: 'rag' })}
                </Typography>

                {/* Category Input */}
                <TextField
                  fullWidth
                  label={t('upload.categoryLabel', { ns: 'rag' })}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={t('upload.categoryPlaceholder', { ns: 'rag' })}
                  sx={{ mb: 3 }}
                />

                {/* Drag & Drop Zone */}
                <Paper
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '3px dashed',
                    borderColor: dragActive
                      ? '#f57c00'
                      : selectedFile
                      ? '#4caf50'
                      : '#d0d0d0',
                    borderRadius: 2,
                    bgcolor: dragActive
                      ? '#fff3e0'
                      : selectedFile
                      ? '#f1f8f4'
                      : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    mb: 2,
                    '&:hover': {
                      borderColor: '#f57c00',
                      bgcolor: '#fff8e1',
                    },
                  }}
                  onClick={() => document.getElementById('user-file-input').click()}
                >
                  <input
                    id="user-file-input"
                    type="file"
                    hidden
                    accept=".pdf,.md,.txt,.doc,.docx"
                    onChange={handleFileInputChange}
                  />

                  {selectedFile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <FileIcon sx={{ fontSize: 48, color: '#4caf50' }} />
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(selectedFile.size)}
                        </Typography>
                      </Box>
                      <CheckIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        {t('upload.dropzone.title', { ns: 'rag' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('upload.dropzone.supportedHint', { ns: 'rag' })}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Upload Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  startIcon={uploading ? null : <UploadIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(245, 124, 0, 0.4)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  {uploading ? t('upload.uploading', { ns: 'rag' }) : t('upload.button', { ns: 'rag' })}
                </Button>

                {uploading && <LinearProgress sx={{ mt: 2 }} color="warning" />}

                {/* Upload Status */}
                {uploadStatus && (
                  <Alert severity={uploadStatus.type} sx={{ mt: 2 }}>
                    {uploadStatus.message}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Search Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f57c00' }}>
                  🔍 {t('search.title', { ns: 'rag' })}
                </Typography>

                {/* Search Bar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t('search.placeholder', { ns: 'rag' })}
                    disabled={searching}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={searching || !searchQuery.trim()}
                    startIcon={!searching ? <SearchIcon /> : null}
                    sx={{
                      minWidth: 120,
                      bgcolor: '#f57c00',
                      '&:hover': { bgcolor: '#e65100' },
                    }}
                  >
                    {searching ? t('search.searching', { ns: 'rag' }) : t('search.button', { ns: 'rag' })}
                  </Button>
                </Box>

                {searching && <LinearProgress sx={{ mb: 2 }} color="warning" />}

                {/* Search Results */}
                {searchResults && (
                  <Box>
                    {searchResults.error ? (
                      <Alert severity="error">{searchResults.error}</Alert>
                    ) : (
                      <>
                        {/* Answer Box */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            mb: 2,
                            background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
                            borderLeft: '4px solid #f57c00',
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f57c00', mb: 1 }}>
                            💡 {t('search.answerTitle', { ns: 'rag' })}
                          </Typography>
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {searchResults.answer || t('search.noAnswer', { ns: 'rag' })}
                          </Typography>
                        </Paper>

                        {/* Sources */}
                        {searchResults.sources && searchResults.sources.length > 0 && (
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                              📑 {t('search.sourcesTitle', { ns: 'rag', count: searchResults.sources.length })}
                            </Typography>
                            {searchResults.sources.map((source, idx) => (
                              <Paper
                                key={idx}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  mb: 1.5,
                                  bgcolor: '#f9f9f9',
                                  borderLeft: '3px solid #f57c00',
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Chip
                                    label={t('search.scoreLabel', { ns: 'rag',
                                      score: source.similarity != null
                                        ? (source.similarity * 100).toFixed(1)
                                        : t('common.na'),
                                    })}
                                    size="small"
                                    sx={{ bgcolor: '#f57c00', color: 'white', fontWeight: 600 }}
                                  />
                                  <Chip
                                    label={source.metadata?.category || t('common.na')}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                  {source.content}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Document List */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00' }}>
                    <FolderIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documents.title', { ns: 'rag' })}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadDocuments}
                    disabled={loadingDocuments}
                    sx={{ borderColor: '#f57c00', color: '#f57c00' }}
                  >
                    {t('documents.refresh', { ns: 'rag' })}
                  </Button>
                </Box>

                {loadingDocuments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} color="warning" />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                    <FileIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                    <Typography variant="body1">{t('documents.empty', { ns: 'rag' })}</Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#fafafa' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.filename', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.type', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.size', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.chunks', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.status', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.uploaded', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">
                            {t('documents.table.actions', { ns: 'rag' })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc) => (
                          <TableRow
                            key={doc.id}
                            sx={{
                              '&:hover': { bgcolor: '#f5f5f5' },
                              transition: 'background-color 0.2s',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FileIcon sx={{ color: '#f57c00' }} />
                                <Tooltip title={doc.filename}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {doc.filename}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {doc.file_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatFileSize(doc.file_size)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={doc.chunk_count || 0} size="small" color="warning" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.status}
                                size="small"
                                color={doc.status === 'completed' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(doc.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={t('documents.actions.delete', { ns: 'rag' })}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(doc)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700 }}>
          ⚠️ {t('delete.title', { ns: 'rag' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('delete.prompt', { ns: 'rag' })}
          </DialogContentText>
          {documentToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {documentToDelete.filename}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('delete.cancel', { ns: 'rag' })}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('delete.deleting', { ns: 'rag' }) : t('delete.confirm', { ns: 'rag' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
